import { forwardRef, useId } from "react";
import { useObjectRef } from "react-aria";
import {
  type InputProps as InputPrimitiveProps,
  SearchField as SearchFieldPrimitive,
  type SearchFieldProps as SearchFieldPrimitiveProps,
} from "react-aria-components";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { SearchIcon, CloseIcon } from "@boi/react-icons";

import * as S from "./SearchInput.styled";
import { ReactAriaProviders } from "../ReactAriaProviders";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";
import { OuterFieldV2, type OuterFieldV2Props } from "../OuterFieldV2";
import { IconButtonV2 } from "../IconButtonV2";

export type SearchInputProps = PropsWithOptionalTestID &
  Pick<InputPrimitiveProps, "placeholder"> &
  Omit<SearchFieldPrimitiveProps, "children"> &
  Pick<OuterFieldV2Props, "endSlot"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: InputAlertsProps["alerts"];
  };

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (props, forwardedRef) => {
    const {
      labelProps,
      alerts,
      id,
      placeholder,
      endSlot,
      testID = "search-input",
      ...otherProps
    } = props;

    const { isDisabled } = otherProps;

    const ref = useObjectRef(forwardedRef);

    const fallbackID = useId();

    function focusElement() {
      ref.current?.focus();
    }

    return (
      <ReactAriaProviders
        fieldID={id || fallbackID}
        externallyLabelledBy={props["aria-labelledby"]}
        externallyDescribedBy={props["aria-describedby"]}
        labelProps={labelProps}
        alerts={alerts}
      >
        {({ labelledBy, describedBy }) => (
          <InputWrapper alert={alerts && <InputAlerts alerts={alerts} />}>
            <InputLabelTopV2 {...labelProps}>
              <SearchFieldPrimitive
                {...otherProps}
                id={id || fallbackID}
                aria-labelledby={labelledBy}
                aria-describedby={describedBy}
              >
                {({ state }) => (
                  <OuterFieldV2
                    startSlot={<SearchIcon />}
                    endSlot={endSlot}
                    buttonSlot={
                      !isDisabled &&
                      state.value !== "" && (
                        <IconButtonV2>
                          <CloseIcon />
                        </IconButtonV2>
                      )
                    }
                    focusElement={focusElement}
                  >
                    <S.Input
                      ref={ref}
                      placeholder={placeholder}
                      data-testid={testID}
                    />
                  </OuterFieldV2>
                )}
              </SearchFieldPrimitive>
            </InputLabelTopV2>
          </InputWrapper>
        )}
      </ReactAriaProviders>
    );
  }
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
